import React, { useEffect, useState } from "react"
import { Link, useNavigate, useLocation } from "react-router-dom"
import moment from "moment"
import { getOrganisations } from "../../requesters/Backend/OrganisationRequester"
import Pagination from "../Pagination"

const PER_PAGE = 25

function OrganisationsDashboard({ baseUrl, currentUser }) {
  const location = useLocation()
  const navigate = useNavigate()
  const { pathname, search } = location
  const initialParams = new URLSearchParams(location.search)

  const [organisations, setOrganisations] = useState([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(parseInt(initialParams.get("page") || "1", 10))
  const [query, setQuery] = useState(initialParams.get("search") || "")
  const [debouncedQuery, setDebouncedQuery] = useState(initialParams.get("search") || "")
  const [status, setStatus] = useState(initialParams.get("status") || "all")

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), 300)
    return () => clearTimeout(t)
  }, [query])

  useEffect(() => {
    const params = { page, per_page: PER_PAGE }
    if (status === "active" || status === "inactive") params.status = status
    if (debouncedQuery) params.search = debouncedQuery

    getOrganisations(baseUrl, params, currentUser).then(res => {
      setOrganisations(res?.response || [])
      setTotal(parseInt(res?.headers?.["x-total"] || "0", 10))
      return null
    }).catch(err => {
      // eslint-disable-next-line no-console
      console.log(err)
    })
  }, [page, debouncedQuery, status, baseUrl, currentUser])

  useEffect(() => {
    const params = new URLSearchParams()
    params.set("page", String(page))
    params.set("status", status)
    if (debouncedQuery) params.set("search", debouncedQuery)
    const next = `?${params.toString()}`
    if (search === next) return
    navigate({ pathname, search: next }, { replace: true })
  }, [page, debouncedQuery, status, navigate, pathname, search])

  const handleStatus = event => {
    setStatus(event.target.value)
    setPage(1)
  }

  const handleSearch = event => {
    setQuery(event.target.value)
    setPage(1)
  }

  return (
    <div className="content">
      <div className="container-fluid">
        <div className="page-header">
          <div className="d-flex justify-content-between align-items-center">
            <h2>Organisations</h2>
            <span>
              <Link to="/backend/create-unit" className="btn btn-outline-main btn-sm">+ Add New</Link>
            </span>
          </div>
        </div>
        <div className="table-filters d-inline-flex">
          <input
            type="search"
            className="form-control"
            placeholder="Search Organisation"
            value={query}
            onChange={handleSearch} />
          <select className="form-control" onChange={handleStatus} value={status}>
            <option value="all">Status: All</option>
            <option value="active">Status: Active</option>
            <option value="inactive">Status: Inactive</option>
          </select>
        </div>
        {organisations.length > 0 ? (
          <>
            <div className="list-table-wrap">
              <div className="table-responsive">
                <table className="table list-table">
                  <thead>
                    <tr>
                      <th>Organisation</th>
                      <th>Status</th>
                      <th>Units</th>
                      <th>Created Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {organisations.map(org => (
                      <tr key={org.id}>
                        <td>
                          <Link to={`/backend/organisations/${org.id}`}>{org.name}</Link>
                        </td>
                        <td>
                          {org.status
                            ? <span className={`cell-badge ${org.status === "active" ? "is-active" : "is-inactive"}`}>{org.status}</span>
                            : "—"}
                        </td>
                        <td>{org.units_count}</td>
                        <td className="cell-muted">{org.created_at ? moment(org.created_at).format("DD MMM YYYY") : ""}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <Pagination
              key={`${debouncedQuery}-${status}`}
              totalRows={total}
              pageChangeHandler={setPage}
              rowsPerPage={PER_PAGE}
              initialPage={page} />
          </>
        ) : (
          <p className="text-muted mt-3 mb-0 text-center">No organisations found</p>
        )}
      </div>
    </div>
  )
}

export default OrganisationsDashboard
